import { useState } from 'react'
import { Buildings, PencilSimple, Plus } from '@phosphor-icons/react'
import { Badge, Banner, ChoiceGroup, DataTable, EmptyState, Field, Input, type Column } from '@/ui'
import { useCan } from '@/auth/context'
import { errorMessage } from '../api/core'
import { localToday, useOrgList, useSaveOrg, type OrgRow } from '../api/masterData'
import { ActionButton } from '../components/Can'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { Code } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import {
  accreditationState,
  createOrgBody,
  draftFromOrg,
  emptyOrgDraft,
  normaliseOrgCode,
  orgOk,
  updateOrgBody,
  validateOrgDraft,
  type OrgDraft,
  type SetupOrgKind,
} from '../model/orgForm'
import { serverProblem } from '../model/serverError'

const KIND_LABEL: Record<string, string> = {
  LAB: 'Lab',
  BUYER: 'Buyer',
  OWN: 'Uncompromised',
  PARTNER: 'Partner',
  PROCESSOR: 'Processor',
}

type Editing = { mode: 'create'; kind: SetupOrgKind } | { mode: 'edit'; row: OrgRow }

const editable = (r: OrgRow) => r.kind === 'LAB' || r.kind === 'BUYER'

/**
 * Labs and buyers. A sample can only be sent to a LAB org, a lab report only names a LAB org
 * (422 NOT_A_LAB), and packed stock only goes to a BUYER org (NOT_A_BUYER); the hall phone lists
 * the buyers from here.
 */
export function OrgsPage() {
  const [f, setF, { reset, active }] = useUrlState({ kind: '' })
  const q = useOrgList(f.kind || undefined)
  const can = useCan()
  const [editing, setEditing] = useState<Editing | null>(null)
  const today = localToday()
  const rows = q.data ?? []

  const columns: Column<OrgRow>[] = [
    { id: 'name', header: 'Name', cell: (r) => <span className="font-semibold">{r.name}</span>, sortValue: (r) => r.name },
    { id: 'code', header: 'Code', cell: (r) => (r.code ? <Code>{r.code}</Code> : '—'), sortValue: (r) => r.code ?? '' },
    {
      id: 'kind',
      header: 'Kind',
      cell: (r) => <Badge tone={r.kind === 'LAB' ? 'info' : r.kind === 'BUYER' ? 'accent' : 'neutral'}>{KIND_LABEL[r.kind] ?? r.kind}</Badge>,
      sortValue: (r) => r.kind,
    },
    {
      id: 'accreditation',
      header: 'Accreditation',
      hideBelow: 'sm',
      cell: (r) => {
        if (r.kind !== 'LAB') return <span className="text-muted">—</span>
        const s = accreditationState(r.accreditationValidTo, today)
        return (
          <span className="flex flex-col leading-tight">
            <span className="font-mono">{r.accreditationNo ?? <span className="font-sans text-muted">No number</span>}</span>
            {r.accreditationValidTo && (
              <span className="text-[12px]">
                {s === 'EXPIRED' ? (
                  <Badge tone="danger">Expired {r.accreditationValidTo}</Badge>
                ) : s === 'EXPIRING' ? (
                  <Badge tone="warn">Valid to {r.accreditationValidTo}</Badge>
                ) : (
                  <span className="text-muted">Valid to {r.accreditationValidTo}</span>
                )}
              </span>
            )}
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) =>
        editable(r) ? (
          <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <ActionButton perm="orgs:update" hide size="sm" variant="ghost" leadingIcon={<PencilSimple />} onClick={() => setEditing({ mode: 'edit', row: r })}>
              Edit
            </ActionButton>
          </span>
        ) : null,
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Labs & buyers"
      description="The labs samples are sent to and the customers packed stock is dispatched to. A sample cannot be sent, a lab report cannot be entered, and a pack lot cannot be dispatched until its lab or buyer is listed here."
      actions={
        <span className="flex gap-2">
          <ActionButton perm="orgs:create" variant="secondary" leadingIcon={<Plus />} onClick={() => setEditing({ mode: 'create', kind: 'BUYER' })}>
            Add buyer
          </ActionButton>
          <ActionButton perm="orgs:create" leadingIcon={<Plus />} onClick={() => setEditing({ mode: 'create', kind: 'LAB' })}>
            Add lab
          </ActionButton>
        </span>
      }
    >
      <DataTable<OrgRow>
        caption="Organisations"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={can('orgs:update') ? (r) => editable(r) && setEditing({ mode: 'edit', row: r }) : undefined}
        searchText={(r) => `${r.name} ${r.code ?? ''} ${r.accreditationNo ?? ''}`}
        searchPlaceholder="Name or code"
        empty={
          <EmptyState
            icon={<Buildings />}
            title={active ? 'None of this kind yet' : 'No organisations yet'}
            description="Add the testing lab first: samples cannot be sent without it."
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Kind"
              value={f.kind as 'LAB' | 'BUYER' | ''}
              onChange={(kind) => setF({ kind })}
              options={[
                { value: 'LAB', label: 'Labs' },
                { value: 'BUYER', label: 'Buyers' },
              ]}
            />
          </FilterBar>
        }
      />
      {editing && <OrgDialog editing={editing} onClose={() => setEditing(null)} />}
    </ConsolePage>
  )
}

function OrgDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const edit = editing.mode === 'edit' ? editing.row : null
  const [d, setD] = useState<OrgDraft>(edit ? draftFromOrg(edit) : emptyOrgDraft(editing.mode === 'create' ? editing.kind : 'LAB'))
  const [touched, setTouched] = useState<Set<keyof OrgDraft>>(new Set())
  const save = useSaveOrg({ inline: true })
  const p = validateOrgDraft(d, editing.mode, edit?.accreditationValidTo)
  const touch = (k: keyof OrgDraft) => setTouched((s) => new Set(s).add(k))
  const shown = (k: keyof typeof p) => (touched.has(k) ? p[k] : undefined)
  const err = save.isError ? serverProblem(save.error) : null
  const what = d.kind === 'LAB' ? 'lab' : 'buyer'

  return (
    <FormDialog
      open
      onClose={onClose}
      title={edit ? `Edit ${edit.name}` : `Add ${what}`}
      saveLabel={edit ? 'Save' : `Add ${what}`}
      loading={save.isPending}
      canSave={orgOk(p)}
      onSave={() => save.mutateAsync(edit ? { id: edit.id, body: updateOrgBody(d) } : { body: createOrgBody(d) })}
    >
      {err && (
        <Banner tone="error" title={err.status === 409 ? 'That code is taken' : err.message}>
          {err.status === 409 ? `${err.message}. Pick another code.` : err.details.join(' · ')}
        </Banner>
      )}
      {!edit && (
        <ChoiceGroup<SetupOrgKind>
          label="Kind"
          value={d.kind}
          onChange={(kind) => setD({ ...d, kind })}
          options={[
            { value: 'LAB', label: 'Testing lab' },
            { value: 'BUYER', label: 'Buyer (customer)' },
          ]}
        />
      )}
      <Field label="Name" required error={shown('name')}>
        <Input
          value={d.name}
          onChange={(e) => setD({ ...d, name: e.target.value })}
          onBlur={() => touch('name')}
          maxLength={120}
          placeholder={d.kind === 'LAB' ? 'Eurofins Analytical Services India' : 'Organic Mandi Pvt Ltd'}
          data-autofocus
        />
      </Field>
      {edit ? (
        <Field label="Code" hint="The code and kind cannot change: other codes are built from them.">
          <Input value={d.code} disabled className="font-mono" />
        </Field>
      ) : (
        <Field label="Code" required error={shown('code')} hint="2–12 capital letters or digits, e.g. EUR. Cannot change later.">
          <Input
            value={d.code}
            onChange={(e) => setD({ ...d, code: normaliseOrgCode(e.target.value) })}
            onBlur={() => touch('code')}
            className="font-mono"
            maxLength={12}
            placeholder={d.kind === 'LAB' ? 'EUR' : 'OMANDI'}
          />
        </Field>
      )}
      {d.kind === 'LAB' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Accreditation no. (NABL)" error={shown('accreditationNo')} hint="Printed on the lab's reports">
            <Input
              value={d.accreditationNo}
              onChange={(e) => setD({ ...d, accreditationNo: e.target.value })}
              onBlur={() => touch('accreditationNo')}
              maxLength={40}
              placeholder="TC-5678"
              className="font-mono"
            />
          </Field>
          <Field label="Accreditation valid to" error={p.accreditationValidTo}>
            <Input
              type="date"
              value={d.accreditationValidTo}
              onChange={(e) => setD({ ...d, accreditationValidTo: e.target.value })}
              onBlur={() => touch('accreditationValidTo')}
            />
          </Field>
        </div>
      )}
      <p className="text-[13px] text-muted">Address, contact person and GSTIN are not stored yet; keep them with the agreement.</p>
    </FormDialog>
  )
}
